import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { User } from '@shared/model/auth.models';
import { Subject } from '@shared/model/course.models';
import { AuthService } from '@shared/service/auth.service';
import { CourseService } from '@shared/service/course.service';
import { SharedService } from '@shared/service/shared.service';
import { SharedValidators } from '@shared/validators';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BaseForm, TokenService } from '@common';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BindPhoneComponent, BindPhoneParams } from '@shared/component/bind-phone/bind-phone.component';

/**
 * 个人资料
 *
 * @version 1.0
 * @since 1.0
 */
@Component({
  selector: 'fl-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false,
})
export class ProfileComponent extends BaseForm implements OnInit {
  // 用户信息
  userInfo: User;

  // 当前可切换学阶
  gradeList = [];

  // 所有学科列表
  totalSubjectList: Subject[];

  // 当前学科列表
  subjectList: Subject[];

  // 提示信息
  noSubjectMsg = '如您还没有设置任教科目,请先设置任教科目再进行其他操作';

  // 是否有提示信息
  isHasMsg: boolean;

  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private authSrv: AuthService = inject(AuthService);
  private courseSrv: CourseService = inject(CourseService);
  private sharedSrv: SharedService = inject(SharedService);
  private msgSrv: NzMessageService = inject(NzMessageService);
  private title: Title = inject(Title);
  private tokenSrv: TokenService = inject(TokenService);
  private modalSrv: NzModalService = inject(NzModalService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    super();
    this.title.setTitle('个人资料' + ' - ' + this.sharedSrv.site.siteTitle);
  }

  ngOnInit() {
    this.form = this.buildForm();
    this.loadData();
  }

  async loadData() {
    this.isHasMsg = false;

    let user = (await this.authSrv.getTeacherInfo(this.sharedSrv.user.user_id)) as User;
    this.sharedSrv.user = {
      ...this.sharedSrv.user,
      phone: user.phone,
    };
    this.userInfo = await this.sharedSrv.user;
    this.form.get('username').setValue(this.userInfo.username);
    this.form.get('firstName').setValue(this.userInfo.first_name);
    this.form.get('phone').setValue(this.userInfo.phone);

    this.getSubjectByGrade();
    // OnPush：异步赋值后需主动标记，否则视图不刷新
    this.cdr.markForCheck();
  }

  async getSubjectByGrade() {
    let currentSubjectId = this.userInfo.subject_id;
    this.totalSubjectList = await this.courseSrv.getSubjectList();

    // 设置任教阶段
    const currentSubjectList = this.totalSubjectList.filter(grade => grade.id === currentSubjectId);
    const currentGradeId = currentSubjectList.length > 0 ? currentSubjectList[0].grade : 2;
    this.form.get('grade').setValue(currentGradeId);

    //  获得任教阶段列表
    this.totalSubjectList.forEach(item => {
      if (this.gradeList.filter(grade => grade.gradeId === item.grade).length === 0) {
        this.gradeList.push({ gradeId: item.grade, name: this.getGradeName(item.grade) });
      }
    });

    //  获得任教科目列表
    this.subjectList = this.totalSubjectList.filter(item => item.grade === this.form.get('grade').value);
    this.isHasMsg = currentSubjectId === 0;

    // 设置科目
    currentSubjectId = currentSubjectId === 0 ? this.subjectList[0].id : currentSubjectId;
    this.form.get('subjectId').setValue(currentSubjectId);
    // OnPush：本方法由 loadData 非 await 触发，其异步赋值需主动标记
    this.cdr.markForCheck();
  }

  buildForm(): UntypedFormGroup {
    return this.fb.group({
      username: [''],
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(10)]],
      phone: [{ value: '', disabled: true }],
      grade: [null, [Validators.required]],
      subjectId: [null, [Validators.required]],
      // 后端只校验长度、常见密码和手机号，不限制字符集；横杠等符号也要计入长度，故不能用 \w
      password: ['', Validators.pattern(/\S{6,48}/)],
      password2: ['', SharedValidators.equals('password')],
    });
  }

  onGradeChange(gradeId) {
    if (this.totalSubjectList) {
      this.subjectList = this.totalSubjectList.filter(item => item.grade === gradeId);
      this.form.get('subjectId').setValue(this.subjectList[0].id);
    }
  }

  async onSubmit() {
    if (
      this.form.get('password').value !== this.form.get('password2').value &&
      this.form.get('password').value &&
      this.form.get('password').value !== ''
    ) {
      this.form.get('password2').setValue(this.form.get('password2').value);
    }

    if (!this.form.get('password').value || this.form.get('password').value === '') {
      this.form.get('password').setValue(null);
    }
    if (!this.form.get('password2').value || this.form.get('password2').value === '') {
      this.form.get('password2').setValue(null);
    }

    if (this.formInvalid) {
      return;
    }

    // 保存结果只由成功提示或全局错误提示反馈，避免一次操作弹两个 toast
    await this.authSrv.updateProfile(
      this.form.get('username').value,
      this.form.get('firstName').value,
      this.form.get('subjectId').value,
      this.form.get('password').value
    );

    // 如果老师修改了学科,,清掉相应的缓存
    if (this.sharedSrv.user.subject_id !== this.form.get('subjectId').value) {
      const user = this.sharedSrv.user;
      const token = this.tokenSrv.jwtToken;
      this.sharedSrv.clearAll();
      // 保留用户信息
      this.sharedSrv.user = user;
      // 保留token
      this.tokenSrv.jwtToken = token;
    }

    this.sharedSrv.user = {
      ...this.sharedSrv.user,
      username: this.form.get('username').value,
      first_name: this.form.get('firstName').value,
      subject_id: this.form.get('subjectId').value,
    } as User;

    this.userInfo.subject_id = this.form.get('subjectId').value;
    this.msgSrv.success('保存成功.');
    // OnPush：await 续体内改动了 userInfo，需主动标记
    this.cdr.markForCheck();
  }

  private getGradeName(grade: number) {
    let name = '';
    switch (grade) {
      case 0:
        name = '小学';
        break;
      case 1:
        name = '初中';
        break;
      case 2:
        name = '高中';
        break;
      default:
        break;
    }
    return name;
  }

  disableFixSubject() {
    if (this.sharedSrv.site.config && this.sharedSrv.site.config.asContentProvider) {
      return false;
    }
    return this.userInfo === undefined ? true : !this.userInfo.isVip && this.userInfo.subject_id;
  }

  /**
   * 对手机号进行脱敏处理，将中间4位替换为*
   * @param phone 完整的手机号
   * @returns 脱敏后的手机号
   */
  private maskPhoneNumber(phone: string): string {
    if (!phone || phone.length !== 11) {
      return phone; // 如果手机号格式不正确，直接返回原值
    }
    // 保留前3位和后4位，中间4位替换为*
    return phone.substring(0, 3) + '****' + phone.substring(7);
  }

  changePhone() {
    const modal = this.modalSrv.create({
      nzTitle: this.userInfo.phone ? '更换手机号' : '绑定手机号',
      nzContent: BindPhoneComponent,
      nzFooter: null,
      nzData: {
        type: 'update',
        userInfo: this.userInfo,
      },
      nzWidth: '400px',
    });
    modal.afterClose.subscribe((params: BindPhoneParams) => {
      if (!params.phone) return;
      // 对手机号进行脱敏处理：将中间4位替换为*
      const maskedPhone = this.maskPhoneNumber(params.phone);
      this.sharedSrv.user = {
        ...this.userInfo,
        phone: maskedPhone,
      };
      this.loadData();
    });
  }
}
